import React, { useState, useEffect, useCallback } from "react";
import '../styles/HeadToHead.css';
import { FaPlus, FaSignInAlt } from "react-icons/fa";
import HeadToHeadCreate from "../components/HeadToHeadCreate";
import HeadToHeadJoin from "../components/HeadToHeadJoin";

function HeadToHead() {
  const [currentGameweek, setCurrentGameweek] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const userId = localStorage.getItem("userId");
  const [openMatches, setOpenMatches] = useState([]);
  const [activeMatches, setActiveMatches] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [points, setPoints] = useState({});

  const fetchPointsForMatches = useCallback(async (matches) => {
    const results = {};
    await Promise.all(
      matches.map(async (match) => {
        try {
          const [creatorRes, opponentRes] = await Promise.all([
            fetch(`${process.env.REACT_APP_API_URL}/api/points/h2h/${match.creatorFplId}`),
            fetch(`${process.env.REACT_APP_API_URL}/api/points/h2h/${match.opponentFplId}`),
          ]);
          const creatorData = await creatorRes.json();
          const opponentData = await opponentRes.json();
          results[match._id] = {
            creator: creatorData.points ?? 0,
            opponent: opponentData.points ?? 0,
            creatorChip: creatorData.chipUsed ?? false,
            opponentChip: opponentData.chipUsed ?? false,
          };
        } catch {
          results[match._id] = { creator: 0, opponent: 0, creatorChip: false, opponentChip: false };
        }
      })
    );
    setPoints(results);
  }, []);

  const fetchOpenMatches = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/h2h/matches?userId=${userId}`);
      const data = await res.json();
      if (data.success) setOpenMatches(data.matches);
    } catch (err) {
      console.error("Error fetching open matches:", err);
    }
  }, [userId]);

  const fetchActiveMatches = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/h2h/active?userId=${userId}`);
      const data = await res.json();
      if (data.success) {
        setActiveMatches(data.matches);
        await fetchPointsForMatches(data.matches);
      }
    } catch (err) {
      console.error("Error fetching active matches:", err);
    }
  }, [userId, fetchPointsForMatches]);

  const fetchAllMatches = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/h2h/all`);
      const data = await res.json();
      if (data.success) {
        setAllMatches(data.matches);
        await fetchPointsForMatches(data.matches);
      }
    } catch (err) {
      console.error("Error fetching all matches:", err);
    }
  }, [fetchPointsForMatches]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/current-gameweek`)
      .then(res => res.json())
      .then(data => setCurrentGameweek(data.currentGameweek))
      .catch(err => console.error("Error fetching gameweek:", err));
  }, []);

  useEffect(() => {
    fetchAllMatches();
    if (userId) {
      fetchOpenMatches();
      fetchActiveMatches();
    }
  }, [userId, fetchOpenMatches, fetchActiveMatches, fetchAllMatches]);

  return (
    <div className="h2h-page">

      {/* ── LEFT PANEL: live matches feed ── */}
      <div className="h2h-left">
        <p className="h2h-open-title">Live Matches</p>
        <div className="h2h-feed">
          {allMatches.length === 0 ? (
            <p className="h2h-empty-state">No active matches.</p>
          ) : (
            allMatches.map((match) => (
              <div key={match._id} className="h2h-feed-card">
                <div className="h2h-feed-scoreline">
                  <span className="h2h-feed-name">{match.creatorName}</span>
                  <div className="h2h-feed-scores">
                    <div className="h2h-feed-score-box">{points[match._id]?.creator ?? '—'}</div>
                    <span className="h2h-feed-vs">vs</span>
                    <div className="h2h-feed-score-box">{points[match._id]?.opponent ?? '—'}</div>
                  </div>
                  <span className="h2h-feed-name h2h-feed-name--right">
                    {match.opponentName || '???'}
                  </span>
                </div>
                <div className="h2h-feed-meta">
                  <span>GW {match.gameweek}</span>
                  <span>KES {match.amount}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── CENTRE: main content ── */}
      <div className="h2h-main">
        <div className="h2h-heading">
          <span className="h2h-badge">{currentGameweek ? `Gameweek ${currentGameweek}` : "Loading..."}</span>
          <h1>Head to Head</h1>
        </div>

        <div className="h2h-rules-card">
          <p className="h2h-rules-title">How it works</p>
          <ul className="h2h-rules-list">
            <li>Play one-on-one with another user for a stake of your choice</li>
            <li>Incase of use of any Fpl Chip your opponent automatically wins that round</li>
            <li>You can join more than one match in the same gameweek</li>
          </ul>
        </div>

        <div className="h2h-buttons">
          <button className="h2h-btn" onClick={() => setShowCreate(true)}>
            <div className="h2h-btn-icon h2h-btn-icon--create">
              <FaPlus size={16} />
            </div>
            <span className="h2h-btn-label">Create match</span>
            <span className="h2h-btn-sub">Set your stake</span>
          </button>

          <button className="h2h-btn" onClick={() => setShowJoin(true)}>
            <div className="h2h-btn-icon h2h-btn-icon--join">
              <FaSignInAlt size={16} />
            </div>
            <span className="h2h-btn-label">Join match</span>
            <span className="h2h-btn-sub">Enter a code</span>
          </button>
        </div>

        {openMatches.length > 0 && (
          <div className="h2h-open-panel-mobile">
            <p className="h2h-open-title">Waiting for Opponent</p>
            {openMatches.map((match) => (
              <div key={match._id} className="h2h-open-card">
                <div className="h2h-open-row">
                  <span className="h2h-open-label">Amount</span>
                  <span className="h2h-open-value">KES {match.amount}</span>
                </div>
                <div className="h2h-open-row">
                  <span className="h2h-open-label">Gameweek</span>
                  <span className="h2h-open-value">GW {match.gameweek}</span>
                </div>
                <div className="h2h-open-code">
                  <span className="h2h-open-label">Match Code</span>
                  <div className="h2h-open-code-row">
                    <span className="h2h-code">{match.matchCode}</span>
                    <button
                      className="h2h-copy-btn"
                      onClick={() => navigator.clipboard.writeText(match.matchCode)}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="h2h-divider" />

        <div className="h2h-active">
          <p className="h2h-active-title">Your active matches</p>
          <p className="h2h-active-subtitle">Click username to see opponent's team</p>
          {activeMatches.length === 0 ? (
            <p className="h2h-empty-state">No active matches yet. Create or join one above.</p>
          ) : (
            activeMatches.map((match) => {
              const isCreator = match.creatorId?.toString() === userId;
              const matchPoints = points[match._id] || { creator: 0, opponent: 0 };
              const myPoints = isCreator ? matchPoints.creator : matchPoints.opponent;
              const opponentPoints = isCreator ? matchPoints.opponent : matchPoints.creator;
              const myFplId = isCreator ? match.creatorFplId : match.opponentFplId;
              const opponentFplId = isCreator ? match.opponentFplId : match.creatorFplId;
              const myName = isCreator ? match.creatorName : match.opponentName;
              const opponentName = isCreator ? match.opponentName : match.creatorName;
              const myChip = isCreator ? matchPoints.creatorChip : matchPoints.opponentChip;
              const opponentChip = isCreator ? matchPoints.opponentChip : matchPoints.creatorChip;

              return (
                <div key={match._id} className="h2h-active-card">
                  <div className="h2h-active-scoreline">
                    <a
                      className="h2h-active-username h2h-active-username--link"
                      href={`https://fantasy.premierleague.com/entry/${myFplId}/event/${match.gameweek}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {myName}
                    </a>
                    {myChip && <span className="h2h-chip-warning">⚠️ Chip used</span>}
                    <div className="h2h-active-scores">
                      <div className="h2h-score-box">
                        <span className="h2h-score-number">{myPoints}</span>
                        <span className="h2h-score-label">points</span>
                      </div>
                      <span className="h2h-score-hyphen">—</span>
                      <div className="h2h-score-box">
                        <span className="h2h-score-number">{opponentPoints}</span>
                        <span className="h2h-score-label">points</span>
                      </div>
                    </div>
                    <a
                      className="h2h-active-username h2h-active-username--right h2h-active-username--link"
                      href={`https://fantasy.premierleague.com/entry/${opponentFplId}/event/${match.gameweek}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {opponentName}
                    </a>
                    {opponentChip && <span className="h2h-chip-warning">⚠️ Chip used</span>}
                  </div>
                  <div className="h2h-active-footer">
                    <div className="h2h-active-meta">
                      <span>GW {match.gameweek}</span>
                      <span>KES {match.amount}</span>
                    </div>
                    <span className={`h2h-role-badge ${isCreator ? "h2h-role-badge--creator" : "h2h-role-badge--opponent"}`}>
                      {isCreator ? "Creator" : "Opponent"}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── RIGHT PANEL: open matches ── */}
      {openMatches.length > 0 && (
        <div className="h2h-open-panel">
          <p className="h2h-open-title">Waiting for Opponent</p>
          {openMatches.map((match) => (
            <div key={match._id} className="h2h-open-card">
              <div className="h2h-open-row">
                <span className="h2h-open-label">Amount</span>
                <span className="h2h-open-value">KES {match.amount}</span>
              </div>
              <div className="h2h-open-row">
                <span className="h2h-open-label">Gameweek</span>
                <span className="h2h-open-value">GW {match.gameweek}</span>
              </div>
              <div className="h2h-open-code">
                <span className="h2h-open-label">Match Code</span>
                <div className="h2h-open-code-row">
                  <span className="h2h-code">{match.matchCode}</span>
                  <button
                    className="h2h-copy-btn"
                    onClick={() => navigator.clipboard.writeText(match.matchCode)}
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <HeadToHeadCreate
          onClose={() => {
            setShowCreate(false);
            fetchOpenMatches();
            fetchActiveMatches();
          }}
        />
      )}

      {showJoin && (
        <HeadToHeadJoin
          onClose={() => {
            setShowJoin(false);
            fetchOpenMatches();
            fetchActiveMatches();
          }}
        />
      )}
    </div>
  );
}

export default HeadToHead;